import {
  makeStyles,
  mergeClasses,
  shorthands,
  tabClassNames,
  useTabStyles_unstable,
} from "@fluentui/react-components";
import {
  pivotHeaderClassName,
  pivotHeaderLabelClassname,
} from "@1js/acui-pivot";
import type { TabState } from "./Tab.types";
import { customTokens } from "@1js/acui-theme";

export const ribbonTabCSSVars = {
  ribbonTabHeight: `--acui-ribbon-tab--height`,
};

const contentSelector = `& .${tabClassNames.content}`;
const hoverContentSelector = `:hover .${tabClassNames.content}`;
const activeContentSelector = `:active .${tabClassNames.content}`;

const { ribbonTabHeight } = ribbonTabCSSVars;

const useStyles = makeStyles({
  root: {
    height: `var(${ribbonTabHeight})`,
    ...shorthands.margin("2px"),
    [":hover"]: {
      backgroundColor: customTokens.colorTransparentBackground,
    },
    [":active"]: {
      backgroundColor: customTokens.colorTransparentBackground,
    },
  },
  contextualTab: {
    [contentSelector]: {
      color: customTokens.colorBrandForeground1,
      fontWeight: customTokens.fontWeightSemibold,
    },
    [hoverContentSelector]: {
      color: customTokens.colorBrandForeground1,
    },
    [activeContentSelector]: {
      color: customTokens.colorBrandForeground1,
    },
  },
  contextualTabDisabled: {
    [contentSelector]: {
      color: customTokens.colorNeutralForegroundDisabled,
    },
  },
  content: {},
  rootNoIndicator: {
    "::after": {
      display: "none",
    },
  },
  icon: {
    height: "16px",
    width: "16px",
    fontSize: "16px",
    alignItems: "baseline",
  },
});

export const useTabStyles = (state: TabState): TabState => {
  const {
    tabType,
    selected,
    hideSelectedIndicator,
    useNormalStyleForContextualTabs = false,
  } = state;

  const styles = useStyles();

  useTabStyles_unstable(state);

  state.root.className = mergeClasses(
    // Scaling to determine top level vs overflow items requires the pivot header class names.
    pivotHeaderClassName,
    styles.root,
    selected && hideSelectedIndicator && styles.rootNoIndicator,
    state.root.className
  );

  if (tabType === "contextual" && !useNormalStyleForContextualTabs) {
    state.root.className = mergeClasses(
      state.root.className,
      styles.contextualTab,
      state.disabled && styles.contextualTabDisabled
    );
  }

  state.content.className = mergeClasses(
    // Scaling to determine top level vs overflow items requires the pivot header class names.
    pivotHeaderLabelClassname,
    state.content.className,
    styles.content
  );

  if (state.icon) {
    state.icon.className = mergeClasses(state.icon.className, styles.icon);
  }

  return state;
};
